import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HOW_IT_WORKS_SECTIONS, HELP_ANCHORS } from '../../constants/helpContent';
import styles from './HowItWorks.module.css';

function AccordionItem({ section, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyRef = useRef(null);

    // Scroll into view when opened via anchor
    useEffect(() => {
        if (defaultOpen && bodyRef.current) {
            setTimeout(() => {
                bodyRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 120);
        }
    }, [defaultOpen]);

    return (
        <div
            className={styles.accordion}
            id={section.anchor}
            ref={bodyRef}
        >
            <button
                className={styles.accordionHeader}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <span className={styles.accordionEmoji}>{section.emoji}</span>
                <span className={styles.accordionTitle}>{section.title}</span>
                <span className={`${styles.accordionChevron} ${open ? styles.chevronOpen : ''}`}>›</span>
            </button>

            {open && (
                <div className={styles.accordionBody}>
                    {section.body?.map((line, i) =>
                        line === '' ? (
                            <div key={i} className={styles.spacer} />
                        ) : (
                            <p key={i} className={styles.bodyText}>{line}</p>
                        ),
                    )}

                    {/* Tier table */}
                    {section.tiers && (
                        <div className={styles.table}>
                            {section.tiers.map((tier) => (
                                <div key={tier.label} className={styles.tableRow}>
                                    <span className={styles.tierEmoji}>{tier.emoji}</span>
                                    <div className={styles.tierInfo}>
                                        <span className={styles.tierLabel}>{tier.label}</span>
                                        <span className={styles.tierDesc}>{tier.description}</span>
                                    </div>
                                    <span className={styles.tierScore}>{tier.score}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Series Mastery table */}
                    {section.masteryTable && (
                        <div className={styles.table}>
                            {section.masteryTable.map((row) => (
                                <div key={row.range} className={styles.tableRow}>
                                    <span className={styles.masteryRange}>{row.range}</span>
                                    <span className={styles.masteryRequired}>{row.required}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Legendary highlight */}
                    {section.highlight && (
                        <div className={styles.highlight}>
                            <p className={styles.highlightText}>{section.highlight}</p>
                        </div>
                    )}

                    {/* Footer note */}
                    {section.footer && (
                        <p className={styles.footerNote}>{section.footer}</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function HowItWorks() {
    const [searchParams] = useSearchParams();
    const anchor = searchParams.get('section');

    const defaultOpenIds = anchor
        ? HOW_IT_WORKS_SECTIONS.filter((s) => s.anchor === anchor).map((s) => s.id)
        : [];

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <h1 className={styles.pageTitle}>How It Works</h1>
                <p className={styles.intro}>
                    How quizzes, coins and collections work on Aluvo — plus rankings, Aluvo Picks and your watchlist.
                </p>

                <div className={styles.sections}>
                    {HOW_IT_WORKS_SECTIONS.map((section) => (
                        <AccordionItem
                            key={section.id}
                            section={section}
                            defaultOpen={defaultOpenIds.includes(section.id)}
                        />
                    ))}
                </div>

                <p className={styles.versionNote}>Rules last updated November 2026</p>
            </div>
        </div>
    );
}